import { Button, Flex } from "@wayflyer/flyui";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import { useIntl } from "react-intl";
import { useLocation, useSearchParams } from "react-router";
import { WayflyerError } from "shared-ui";

import { getAirOriginPath } from "../../../../utils/air-origin-path";
import { COMMON_MESSAGES } from "../../../common-messages";
import {
  AmazonConnect,
  BigcommerceConnectOtp,
  MagentoConnect,
  MockifyConnect,
  ShopifyAppStoreConnect,
  ShopifyConnect,
  WooCommerceConnect,
} from "../../../connect";
import { type AirResponseInFlightData } from "../../../constants";
import { AIR_IDENTIFIER_STORAGE_KEY, getPostAIRConnectionPath } from "../../utils";

import type { Step } from "./constants";

interface ConnectFormProps {
  connectorSlug: string;
  airPublicIdentifier: string;
  setQuestionaireStep: (step: Step) => void;
}

export const ConnectForm = ({ connectorSlug, airPublicIdentifier, setQuestionaireStep }: ConnectFormProps) => {
  const intl = useIntl();
  const { pathname } = useLocation();
  const airOrigin = getAirOriginPath(pathname);
  const [, setSearchParams] = useSearchParams();
  const postConnectPath = getPostAIRConnectionPath(airOrigin);

  const airResponseInFlightData: AirResponseInFlightData = {
    type: "additional_revenue_source",
    airPublicIdentifier: airPublicIdentifier,
    expiry: dayjs().add(5, "minutes").toISOString(),
  };

  const EcommercePlatformComponents: Record<string, ReactNode> = (() => {
    const platformProps = {
      postConnectPath,
      onInitiateConnection: () =>
        localStorage.setItem(AIR_IDENTIFIER_STORAGE_KEY, JSON.stringify(airResponseInFlightData)),
    };
    return {
      shopify2: <ShopifyConnect {...platformProps} />,
      "shopify-appstore": <ShopifyAppStoreConnect {...platformProps} />,
      amazonrutter: <AmazonConnect {...platformProps} />,
      woocommerce2: <WooCommerceConnect {...platformProps} />,
      bigcommercerutter: <BigcommerceConnectOtp {...platformProps} />,
      magento2: <MagentoConnect {...platformProps} />,
      mockify: <MockifyConnect {...platformProps} />,
    };
  })();

  if (!(connectorSlug in EcommercePlatformComponents)) {
    throw new WayflyerError("Missing or unknown platform in query params", "404");
  }

  return (
    <Flex direction="column" gap="8">
      {connectorSlug && EcommercePlatformComponents[connectorSlug]}
      <Flex>
        <Button
          variant="Ghost"
          onClick={() => {
            setSearchParams({});
            setQuestionaireStep("select_additional_source");
          }}
          data-dd-action-name="air-revenue-source-connect-back"
        >
          {intl.formatMessage(COMMON_MESSAGES.backLabel)}
        </Button>
      </Flex>
    </Flex>
  );
};
